/*----------------------------------
- HOOK: LAZY PREVIEW DRAWER
----------------------------------*/

/**
 * Moved out of /domains (batch 7) with the old frame's look files, which batch 4 deleted once /registrars
 * and /auctions moved to the marketing `Drawer`. This one is logic, not look: /domains and Home read it too,
 * and so do the /registrars and /auctions drawers.
 *
 * @docs docs/features/product-domain-listings
 * @docs docs/features/auctions
 * @adr  ADR-0028
 * @rule A public preview drawer's chunk is requested only once a row is opened, so its cards and sheet never
 *       reach the page's eager chunk (the eleven cards and `domainsDrawer.css` for /domains since batch 7
 *       part 3, the auction and registrar drawers with their sheets since batch 4). A failed chunk is
 *       forgotten and the row cleared with reason `error`, so the next row click requests it again.
 *       One loader and one hook for every public preview drawer (`/domains*`, Home, `/auctions`, `/registrars`);
 *       each lazy drawer only names its chunk and the prop its subject travels in.
 */

/*----------------------------------
- DEPENDANCES
----------------------------------*/

import React from 'react';

import { appErrorReporter } from '@/common/libs/errorReporting';

/*----------------------------------
- TYPES
----------------------------------*/

/**
 * Why a public preview drawer closed, recorded on its dismiss event. Here, beside the two hooks every preview drawer
 * shares, where it moved before batch 4 deleted the old frame's look files (review m-11). `drag` was the old frame's
 * grab handle, gone with that frame: no drawer sends it now, and the union keeps it for the events already recorded.
 * `error`: the lazy loader failed to fetch the drawer chunk, so it clears the row itself.
 */
export type PreviewDrawerCloseReason = 'escape' | 'backdrop' | 'close-button' | 'drag' | 'cta' | 'error';

export type PreviewDrawerComponent<TProps> = (props: TProps) => React.JSX.Element | null;

export type PreviewDrawerLoader<TProps> = {
    /** One request per page visit, shared by every caller. */
    load: () => Promise<PreviewDrawerComponent<TProps>>;
    /** Drops a failed request, so the next open asks for the chunk again. */
    forget: () => void;
};

/*----------------------------------
- LOADER
----------------------------------*/

/** The drawer chunk behind a dynamic `import()`, requested at most once at a time. */
export const createPreviewDrawerLoader = <TProps>(importDrawer: () => Promise<{ default: PreviewDrawerComponent<TProps> }>): PreviewDrawerLoader<TProps> => {
    let drawerModule: Promise<PreviewDrawerComponent<TProps>> | null = null;
    return {
        load: () => {
            drawerModule ??= importDrawer().then((module) => module.default);
            return drawerModule;
        },
        forget: () => {
            drawerModule = null;
        },
    };
};

/*----------------------------------
- HOOK
----------------------------------*/

/**
 * The loaded drawer, the subject to draw and whether it is open. The drawer keeps drawing the
 * subject it closed on while its exit plays, as the Product's does.
 */
export const useLazyPreviewDrawer = <TSubject, TProps>(
    loader: PreviewDrawerLoader<TProps>,
    subject: TSubject | null,
    onRequestClose: (reason: PreviewDrawerCloseReason) => void,
) => {
    const [Drawer, setDrawer] = React.useState<PreviewDrawerComponent<TProps> | null>(null);
    const [lastSubject, setLastSubject] = React.useState<TSubject | null>(subject);
    const isOpen = subject !== null;

    React.useEffect(() => {
        if (subject !== null) setLastSubject(subject);
    }, [subject]);

    React.useEffect(() => {
        if (!isOpen || Drawer) return;

        let disposed = false;
        loader.load()
            .then((component) => {
                if (!disposed) setDrawer(() => component);
            })
            .catch((error) => {
                // A failed chunk is forgotten and the preview cleared, reason `error`: the dismiss
                // event records the failure, and since the subject goes back to null the next row
                // click reopens the drawer and requests the chunk again.
                loader.forget();
                if (!disposed) onRequestClose('error');
                appErrorReporter.app.handleError(error);
            });
        return () => {
            disposed = true;
        };
    }, [Drawer, isOpen, loader, onRequestClose]);

    return { Drawer, drawn: subject ?? lastSubject, isOpen };
};
